import Footer from "../footer/footer"; 
import Navbar from "../navbar/navbar"; 
import { Helmet } from "react-helmet";
import React from "react";
import { 
  FaIndustry, 
  FaTools, 
  FaWater, 
  FaFlask, 
  FaCogs, 
  FaChartLine, 
  FaShieldAlt, 
  FaHeadset, 
  FaCubes, 
  FaCheck 
} from "react-icons/fa"; 
import { HiOutlineBadgeCheck } from "react-icons/hi"; 
import { FaOilWell } from "react-icons/fa6";

// Import flowmeter images
import turbine from '../../assets/Turbine Flowmeter.png'; 
import helix from '../../assets/Helix Rotor Flow meter.png'; 
import coriolis from '../../assets/Coriolis Mass Flow meter.png'; 
import oval from '../../assets/oval gear.jpg';
import flowBanner from '../../assets/Flowmeter.jpg'; 

const Flowmeter: React.FC = () => {
  const flowmeterProducts = [
    {
      name: "Turbine Flowmeter",
      description: "Measures flow rate using rotor rotation proportional to fluid velocity with high accuracy.",
      image: turbine,
      specs: [
        "Accuracy: ±0.5% to ±0.1% of reading",
        "Materials: 316SS, Hastelloy, Titanium",
        "Size Range: 1/2\" to 24\"",
        "Output: 4-20mA, Pulse, Modbus"
      ],
      details: "Our turbine flowmeters feature dual-bearing designs with sapphire bearings for extended service life. With K-factor linearization and temperature compensation, they deliver precise measurement of clean liquids and gases in custody transfer, pipeline monitoring, and fuel dispensing applications."
    },
    {
      name: "Helix Rotor Flowmeter",
      description: "Uses helical rotors for accurate measurement of viscous fluids and varying flow conditions.",
      image: helix,
      specs: [
        "Viscosity Range: Up to 100,000 cP",
        "Accuracy: ±0.2% of rate",
        "Pressure Rating: ANSI 150 to 600",
        "Temperature: -40°C to 200°C"
      ],
      details: "Engineered with helical screw rotors and zero-pulse technology, our flowmeters handle challenging fluids like crude oil, asphalt, and polymer solutions. Featuring wear-compensating rotors and non-intrusive verification, they ensure long-term accuracy in refinery, chemical, and food processing applications."
    },
    {
      name: "Oval Gear Flowmeter",
      description: "Positive displacement meter using meshing oval gears for precise volumetric measurement.",
      image: oval,
      specs: [
        "Flow Range: 0.05 to 300 GPM",
        "Accuracy: ±0.5% of reading",
        "Materials: Cast Iron, Bronze, 316SS",
        "Viscosity: 1 to 100,000 cP"
      ],
      details: "Our oval gear meters feature hardened stainless steel gears with PTFE seals for leak-free operation. With magnetic coupling and optical sensors, they provide pulse-free flow measurement of lubricating oils, solvents, and additives without fluid contact to the electronics."
    },
    {
      name: "Coriolis Mass Flowmeter",
      description: "Measures mass flow directly using Coriolis effect for unmatched accuracy and density measurement.",
      image: coriolis,
      specs: [
        "Accuracy: ±0.1% of mass flow rate",
        "Density Accuracy: ±0.0005 g/cm³",
        "Temperature Range: -240°C to 350°C",
        "Pressure Rating: Up to 1000 bar"
      ],
      details: "Featuring single or dual-tube designs with advanced signal processing, our Coriolis meters maintain accuracy in multiphase flows. With multivariable measurement (mass flow, density, temperature) and diagnostic capabilities, they're ideal for custody transfer, batching, and reactor feed applications."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Industrial Flowmeters Manufacturer & Exporter | Turbine, Helix Rotor, Oval Gear & Coriolis </title>
        <meta 
          name="description" 
          content="City Cat International – Manufacturer & exporter of industrial flowmeters: Turbine, Helix Rotor, Oval Gear & Coriolis for oil, gas & process industries worldwide." 
        />

        <meta 
          name="keywords" 
          content="industrial flowmeters manufacturer India, turbine flowmeter exporter, helix rotor flowmeter supplier, oval gear flowmeter manufacturer India, coriolis mass flowmeter exporter, oil and gas flow measurement equipment, industrial instrumentation exporter, City Cat International, process flowmeters India, industrial flowmeter supplier" 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.oilandgasindustrialequipmentsupply.com/flowmeter" />
        
        <meta property="og:title" content="Precision Flow Measurement Solutions | City Cat International" />
        <meta property="og:description" content="Exporting API-certified flowmeters to 40+ countries. 25+ years expertise in flow measurement for oil & gas, chemical, and process industries." />
        <meta property="og:image" content="https://www.oilandgasindustrialequipmentsupply.com/assets/flowmeter-banner.jpg" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Flowmeters for Process Control | City Cat International" />
        <meta name="twitter:description" content="Global supplier of flow measurement solutions including Turbine, Coriolis, Oval Gear, and Helix Rotor meters." />
        
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "ProductPage",
            "name": "Industrial Flowmeters",
            "description": "Manufacturer and exporter of precision flow measurement instruments",
            "url": "https://www.oilandgasindustrialequipmentsupply.com/flowmeter",
            "image": "https://www.oilandgasindustrialequipmentsupply.com/assets/flowmeter-banner.jpg",
            "brand": {
              "@type": "Brand",
              "name": "City Cat International"
            },
            "manufacturer": {
              "@type": "Organization",
              "name": "City Cat International"
            }
          }
          `}
        </script>
      </Helmet>
      <Navbar />
      
      {/* Hero Banner */}
      <div className="relative bg-gray-900 mt-24">
          <img 
              src={flowBanner} 
              alt="Industrial Flowmeters for Process Control" 
              className="w-full h-96 object-cover opacity-70"
          />
          <div className="absolute inset-0 flex flex-col justify-center items-center text-center px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Precision Flow Measurement Solutions
              </h1>
              <p className="text-xl text-white max-w-3xl">
                  Turbine, Coriolis, Oval Gear & Helix Rotor Flowmeters for Industrial Applications
              </p>
          </div>
      </div>

      {/* Industries Served Section */}
      <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                  Flowmeters for Critical Industries
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
                  {[
                      { icon: <FaOilWell />, name: "Oil & Gas" },
                      { icon: <FaWater />, name: "Water Treatment" },
                      { icon: <FaIndustry />, name: "Chemical" },
                      { icon: <FaTools />, name: "Power Generation" },
                      { icon: <FaFlask />, name: "Pharmaceutical" },
                      { icon: <FaOilWell />, name: "Food & Beverage" }
                  ].map((industry, index) => (
                      <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center">
                          <div className="text-rose-600 text-3xl mb-3 flex justify-center">
                              {industry.icon}
                          </div>
                          <h3 className="font-semibold text-gray-800">{industry.name}</h3>
                      </div>
                  ))}
              </div>
          </div>
      </section>

      {/* Comprehensive Solutions Section */}
      <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                  Flow Measurement Solutions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
                  {[
                      { 
                          icon: <FaCogs className="text-4xl mb-4" />,
                          title: "Custom Engineering",
                          desc: "Specialized designs for unique process conditions"
                      },
                      { 
                          icon: <FaChartLine className="text-4xl mb-4" />,
                          title: "High Accuracy",
                          desc: "±0.1% to ±0.5% accuracy across range"
                      },
                      { 
                          icon: <FaShieldAlt className="text-4xl mb-4" />,
                          title: "Safety Compliance",
                          desc: "ATEX, IECEx, SIL 2/3 certified"
                      },
                      { 
                          icon: <FaHeadset className="text-4xl mb-4" />,
                          title: "Technical Support",
                          desc: "Flowmeter selection and sizing"
                      },
                      { 
                          icon: <FaCubes className="text-4xl mb-4" />,
                          title: "Calibration Services",
                          desc: "ISO 17025 accredited flow labs"
                      }
                  ].map((feature, index) => (
                      <div key={index} className="bg-gray-50 p-6 rounded-lg text-center">
                          <div className="text-rose-600 flex justify-center">{feature.icon}</div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                          <p className="text-gray-700">{feature.desc}</p>
                      </div>
                  ))}
              </div>
          </div>
      </section>

      <div className="bg-white">
          <h2 className="text-center text-gray-800 text-4xl lg:text-5xl font-bold leading-tight pt-16">
             Flowmeter Product Range
          </h2>
          <div className="my-4 mx-auto w-16 h-2 border-4 border-rose-600 mb-20"></div>

          {/* Flowmeter Product Sections */}
          {flowmeterProducts.map((product, index) => (
            <section key={index} data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}>
              <div className="mx-auto max-w-screen-xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-16">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
                  <div className={`relative h-64 overflow-hidden rounded-lg sm:h-80 lg:h-full ${index % 2 === 0 ? "lg:order-last" : "lg:order-first"}`}>
                    <img
                      alt={`${product.name} for industrial applications`}
                      src={product.image}
                      className="absolute inset-0 h-full w-full object-contain" />
                  </div>
                  <div className="lg:py-16">
                    <h2 className="text-4xl text-gray-800 text-center font-bold sm:text-4xl">{product.name}</h2>
                    
                    <div className="my-6">
                      <h3 className="text-xl font-semibold text-rose-600 mb-2">Key Specifications:</h3>
                      <ul className="list-disc pl-5 text-gray-700 space-y-1">
                        {product.specs.map((spec, i) => (
                          <li key={i}>{spec}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <p className="mt-4 text-gray-600 text-lg text-justify">
                      {product.details}
                    </p>
                    
                    <div className="mt-8 flex justify-center">
                      <a
                        href="https://wa.me/+919819600149"
                        className="inline-block rounded bg-rose-600 px-8 py-3 text-md font-medium text-white transition hover:bg-rose-700 focus:outline-none focus:ring focus:ring-rose-300"
                      >
                        Request Quote
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ))}
      </div>

      {/* Oil & Gas Applications */}
      <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                  Flowmeter Applications in Oil & Gas
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
                  {[
                      {
                          title: "Custody Transfer",
                          content: "Coriolis meters for fiscal measurement"
                      },
                      {
                          title: "Pipeline Monitoring",
                          content: "Turbine meters for product movement"
                      },
                      {
                          title: "Well Testing",
                          content: "Helix rotor meters for multiphase flow"
                      },
                      {
                          title: "Fuel Dispensing",
                          content: "Oval gear meters for accurate batching"
                      },
                      {
                          title: "Chemical Injection",
                          content: "Compact flowmeters for additive control"
                      },
                      {
                          title: "Refinery Processing",
                          content: "High-temperature Coriolis meters"
                      }
                  ].map((app, index) => (
                      <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                          <h3 className="text-xl font-bold text-rose-600 mb-3">{app.title}</h3>
                          <p className="text-gray-700">{app.content}</p>
                      </div>
                  ))}
              </div>
          </div>
      </section>

      {/* Enhanced Technical Support CTA */}
      <section className="py-16 bg-gradient-to-r from-rose-700 to-rose-900 text-white">
          <div className="container mx-auto px-4 text-center">
              <div className="max-w-4xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                      Need Flow Measurement Support?
                  </h2>
                  <p className="text-xl mb-10">
                      Our API-certified engineers provide:
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-12">
                      {[
                          "Meter Selection & Sizing",
                          "Flow Calibration Services",
                          "Installation Guidance",
                          "Commissioning Support",
                          "Hazardous Area Certification",
                          "Performance Verification"
                      ].map((service, index) => (
                          <div key={index} className="flex items-start">
                              <HiOutlineBadgeCheck className="text-2xl mt-1 mr-3 text-rose-200 flex-shrink-0" />
                              <span>{service}</span>
                          </div>
                      ))}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                      <a
                          href="tel:+919819600149"
                          className="bg-white text-rose-700 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition duration-300"
                      >
                          Call: +91 9819600149
                      </a>
                      <a
                          href="mailto:info@oilandgasindustrialequipmentsupply.com"
                          className="border-2 border-white hover:bg-rose-800 font-bold py-3 px-8 rounded-lg transition duration-300"
                      >
                          Email Technical Team
                      </a>
                  </div>
              </div>
          </div>
      </section>

      {/* Project Case Studies */}
      <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
                  Global Flowmeter Projects
              </h2>
              
              <div className="max-w-5xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-8">
                      <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-xl font-bold text-rose-600 mb-3">Offshore Platform Custody Transfer</h3>
                          <p className="text-gray-700 mb-4">
                              <span className="font-semibold">Location:</span> North Sea<br />
                              <span className="font-semibold">Solution:</span> Coriolis Mass Flowmeters<br />
                              <span className="font-semibold">Quantity:</span> 12 units<br />
                              <span className="font-semibold">Features:</span> SIL 2 Certified, Dual-Tube Design
                          </p>
                          <div className="flex items-center">
                              <FaCheck className="text-green-600 mr-2" />
                              <span>±0.1% accuracy in custody transfer operations</span>
                          </div>
                      </div>
                      
                      <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-xl font-bold text-rose-600 mb-3">Crude Oil Pipeline Monitoring</h3>
                          <p className="text-gray-700 mb-4">
                              <span className="font-semibold">Location:</span> Middle East<br />
                              <span className="font-semibold">Solution:</span> Turbine Flowmeters<br />
                              <span className="font-semibold">Quantity:</span> 85 units<br />
                              <span className="font-semibold">Features:</span> Dual-Bearing Design, API 5.7 Compliant
                          </p>
                          <div className="flex items-center">
                              <FaCheck className="text-green-600 mr-2" />
                              <span>5+ years operation with zero calibration drift</span>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      </section>
     
      <Footer />
    </>
  );
};

export default Flowmeter;